import React, { useEffect, useMemo, useRef, useState, Suspense } from "react";
import { useRouteLoaderData, Await } from "react-router-dom";
import { Col, Form, InputGroup, Stack, Table, ButtonGroup, Button } from "react-bootstrap";
import { CaretDownFill, CaretUpFill, Search, CheckLg, XLg, TrashFill, PencilSquare, PlayCircleFill } from "react-bootstrap-icons";
import ConfirmPrompt from "../ConfirmPrompt";
import { axiosInstance } from "../../auth/Auth";
import useSortableData from "../useSortableData";
import ExportData from "../ExportData";
import EmptyTableRow from "../EmptyTableRow";
import moment from "moment/min/moment-with-locales";
import RATooltip from "../RATooltip";
import AudioPlayer from "../AudioPlayer";

const FreestyleDeltasTableDisplay = ({
    freestyleDeltasData,
    setFreestyleDeltasData,
    demo,
    collection,
    setCollection,
    setCollections,
    displayLabels,
    openEditPage,
    setOpenEditPage,
    setAverages,
    showStack,
    setEditCollectionData,
    handleDataRefresh,
}) => {
    const bitNameInputChangeRef = useRef(null);
    const { radios, radiosRecordSettings } = useRouteLoaderData("root");
    const [searchValue, setSearchValue] = useState("");
    const [selectedItem, setSelectedItem] = useState(null);
    const [excludedDates, setExcludedDates] = useState(null);
    const [radioNames, setRadioNames] = useState([]);
    const [errorCode, setErrorCode] = useState(null);
    const [deleteCollectionBit, setDeleteCollectionBit] = useState(null);
    const [deleteCollectionId, setDeleteCollectionId] = useState(null);
    const [bitNameInputValue, setBitNameInputValue] = useState("");
    const [editCollectionName, setEditCollectionName] = useState(false);
    const [collectionNameInputValue, setCollectionNameInputValue] = useState(collection?.[0]?.collection_name || "");

    useEffect(() => {
        async function getRadioNames() {
            const radiosData = (await radios).data;
            setRadioNames(
                radiosData.map(x => {
                    return { radio_id: x.radio_id, radio_name: x.radio_name };
                })
            );
        }
        if (radios) {
            getRadioNames();
        }
    }, [radios]);

    async function getAudioUrl(startDatetime, endDatetime) {
        try {
            const response = await axiosInstance.get(`/mp3service/?radio_id=${collection[0].radio_id}&start_time=${startDatetime}&end_time=${endDatetime}`);
            return response.data;
        } catch (error) {
            console.error(error);
        }
    }

    const getRadioName = radio_id => radioNames.find(x => x.radio_id === radio_id)?.radio_name || "";

    useEffect(() => {
        if (freestyleDeltasData) {
            async function fetchData() {
                try {
                    if (collection[0] && freestyleDeltasData[0] && demo[0]) {
                        const excludedDays = await axiosInstance.get(
                            `/excluded_days/${collection[0].radio_id}/${moment(freestyleDeltasData[0].start_datetime).format("YYYY-MM-DD")}/${moment(
                                freestyleDeltasData[freestyleDeltasData.length - 1].end_datetime
                            ).format("YYYY-MM-DD")}/${demo[0].data_type}/false/true`
                        );
                        if (excludedDays?.data.length > 0) {
                            setExcludedDates(excludedDays.data.join(", "));
                        }
                    }
                } catch (error) {
                    console.error("Error fetching excluded days:", error);
                }
            }
            fetchData();
        }
    }, [freestyleDeltasData, demo, collection]);

    const preprocessData = data => {
        return data.map(item => {
            const startDatetime = moment(item.start_datetime);
            const endDatetime = moment(item.end_datetime);

            return {
                ...item,
                date: startDatetime.format("YYYY-MM-DD"),
                start_time: startDatetime.format("HH:mm:ss"),
                duration: moment.utc(endDatetime.diff(startDatetime)).format("mm:ss"),
            };
        });
    };

    const freestyleDeltasDataMemo = useMemo(() => {
        if (!freestyleDeltasData) {
            return [];
        }
        if (freestyleDeltasData) {
            let filteredData = freestyleDeltasData.flatMap(x =>
                demo.map(demoEntry => {
                    const results = Array.isArray(x.results) ? x.results.filter(result => result.demo_id === demoEntry.data_demo_id) : [];
                    const result =
                        results.length > 0
                            ? results[0]
                            : {
                                  delta: "-",
                                  gain_loss: "-",
                                  impressions: "-",
                                  delta_listeners: "-",
                              };
                    return {
                        collection_id: collection[0]?.collection_id || null,
                        collection_name: collection[0]?.collection_name || null,
                        bit_id: x.bit_id,
                        bit_name: x.bit_name,
                        demo_id: demoEntry.demo_id,
                        listener_group: demoEntry.listener_group,
                        demo_name: demoEntry.data_type === 1 ? `Online: ${demoEntry.name}` : `Cover: ${demoEntry.name}`,
                        data_type: demoEntry.data_type,
                        start_datetime: x.start_datetime,
                        end_datetime: x.end_datetime,
                        song: x.song,
                        results: result,
                    };
                })
            );

            if (searchValue) {
                filteredData = filteredData.filter(
                    x =>
                        x.bit_name.toLowerCase().includes(searchValue.toLowerCase().trim()) ||
                        x.demo_name.toLowerCase().includes(searchValue.toLowerCase().trim()) ||
                        (x.date && x.date.includes(searchValue.trim())) ||
                        (x.start_time && x.start_time.includes(searchValue.trim())) ||
                        (x.duration && x.duration.includes(searchValue.trim()))
                );
            }
            return preprocessData(filteredData);
        }
    }, [freestyleDeltasData, demo, collection, searchValue]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(freestyleDeltasDataMemo);

    const csvHeaders = ["Bit", "Demographic", "Date", "Start Time", "Duration", "Delta", "Gain/Loss", "Impressions", "Delta Listeners"];
    const csvData = [];
    sortedItems?.forEach(x => {
        const result = x.results && x.results.length > 0 ? x.results[0] : x.results;
        const rowData = [
            x.bit_name,
            x.demo_name,
            moment(x.start_datetime).format("YYYY-MM-DD"),
            moment(x.start_datetime).format("HH:mm:ss"),
            moment.utc(moment(x.end_datetime).diff(moment(x.start_datetime))).format("mm:ss"),
            result.delta || "",
            result.gain_loss || "",
            result.impressions || "",
            result.delta_listeners || "",
        ];
        csvData.push(rowData ?? []);
    });

    useEffect(() => {
        if (showStack) {
            // Step 1: Gather all unique demo names from sortedItems
            const allDemoNames = new Set(sortedItems.map(item => item.demo_name));

            // Step 2: Calculate averages for demos with valid deltas
            const demoAverages = sortedItems.reduce((averages, currentItem) => {
                const { demo_name, results } = currentItem;

                // Ensure results.delta is a valid number
                const deltaValue = parseFloat(results?.delta);
                if (!isNaN(deltaValue)) {
                    const existingDemo = averages.find(avg => avg.demo_name === demo_name);
                    if (existingDemo) {
                        existingDemo.totalDelta += deltaValue;
                        existingDemo.count += 1;
                    } else {
                        averages.push({
                            demo_name,
                            totalDelta: deltaValue,
                            count: 1,
                        });
                    }
                }

                return averages;
            }, []);

            // Step 3: Ensure all demos are included in the final averages
            const finalAverages = Array.from(allDemoNames).map(demoName => {
                const foundAverage = demoAverages.find(avg => avg.demo_name === demoName);
                if (foundAverage) {
                    return {
                        demo_name: foundAverage.demo_name,
                        averageDelta: foundAverage.totalDelta / foundAverage.count,
                    };
                } else {
                    return {
                        demo_name: demoName,
                        averageDelta: "-", // No delta, so set to "-"
                    };
                }
            });

            // Update the state with the new averages
            setAverages(finalAverages);
        }
    }, [freestyleDeltasData, showStack, sortedItems]);

    async function updateBitName(bit, new_bit_name) {
        const input = {
            bit_name: new_bit_name,
            start_datetime: bit.start_datetime,
            end_datetime: bit.end_datetime,
        };

        try {
            const response = await axiosInstance.put(`/freestyle_deltas/${bit.collection_id}/bits/${bit.bit_id}`, input);

            const updatedData = freestyleDeltasData.map(entry => {
                if (entry.bit_id === bit.bit_id) {
                    return { ...entry, bit_name: new_bit_name };
                }
                return entry;
            });
            setFreestyleDeltasData(updatedData);
            setSelectedItem(null);
            setErrorCode(null);
            setBitNameInputValue(null);
        } catch (error) {
            setErrorCode(error?.response?.status ?? 500);
        }
    }
    async function deleteBit(bit) {
        console.log("Deleting bit:", bit);
        try {
            const response = await axiosInstance.delete("/freestyle_deltas/" + bit.collection_id + "/bits/" + bit.bit_id);
            const updatedData = freestyleDeltasData.filter(entry => entry.bit_id !== bit.bit_id);
            setFreestyleDeltasData(updatedData);
            setDeleteCollectionBit(null);
            setErrorCode(null);
        } catch (error) {
            setErrorCode(error?.response?.status ?? 500);
        }
    }

    async function updateCollectionName(collection, newCollectionName) {
        const input = {
            radio_id: collection[0].radio_id,
            user_id: collection[0].user_id,
            collection_name: newCollectionName,
        };

        try {
            const response = await axiosInstance.put(`/freestyle_deltas/${collection[0].collection_id}`, input);
            setCollection([
                {
                    ...collection[0],
                    collection_name: newCollectionName,
                },
            ]);

            setEditCollectionName(false);
            setErrorCode(null);
        } catch (error) {
            setErrorCode(error?.response?.status ?? 500);
        }
    }

    async function deleteCollection(collection_id) {
        console.log("Deleting Collection:", collection_id);
        try {
            const response = await axiosInstance.delete("/freestyle_deltas/" + collection_id);
            setFreestyleDeltasData(null);
            setDeleteCollectionId(null);
            setErrorCode(null);
        } catch (error) {
            setErrorCode(error?.response?.status ?? 500);
        }
        handleDataRefresh();
    }

    return (
        <>
            {displayLabels && showStack && <hr className="m-2"></hr>}
            <div className="mt-4">
                {showStack && (
                    <>
                        <Stack gap={1} className=" d-flex flex-xl-row ">
                            <div className="mt-1 d-flex flex-wrap flex-xl-nowrap">
                                <span className="mt-1"> Collection:</span>
                                {editCollectionName ? (
                                    <>
                                        <Form.Control
                                            value={collectionNameInputValue}
                                            autoFocus
                                            onChange={e => setCollectionNameInputValue(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === "Enter") {
                                                    updateCollectionName(collection, collectionNameInputValue);
                                                    setEditCollectionName(false);
                                                } else if (e.key === "Escape") {
                                                    setCollectionNameInputValue(collection[0]?.collection_name || "");
                                                    setEditCollectionName(false);
                                                }
                                            }}
                                        />
                                        <ButtonGroup size="sm">
                                            <Button
                                                variant="success"
                                                title="Save"
                                                onClick={() => {
                                                    updateCollectionName(collection, collectionNameInputValue);
                                                    setEditCollectionName(false);
                                                }}>
                                                <CheckLg />
                                            </Button>
                                            <Button
                                                variant="danger"
                                                title="Discard"
                                                onClick={() => {
                                                    setCollectionNameInputValue(collection[0]?.collection_name || "");
                                                    setEditCollectionName(false);
                                                }}>
                                                <XLg />
                                            </Button>
                                        </ButtonGroup>
                                    </>
                                ) : (
                                    <>
                                        <span className="fw-bold ms-xl-1">
                                            {collection[0]?.collection_name} ({getRadioName(collection[0]?.radio_id)})
                                            <ButtonGroup size="sm">
                                                <Button
                                                    variant=""
                                                    title={"Edit"}
                                                    onClick={() => setEditCollectionName(true)}
                                                    className="freestyle-delta-edit-button ">
                                                    <PencilSquare />
                                                </Button>
                                                <Button
                                                    variant=""
                                                    title={"Delete"}
                                                    onClick={() => setDeleteCollectionId(collection[0]?.collection_id)}
                                                    className="freestyle-delta-delete-button ">
                                                    <TrashFill className="text-danger" />
                                                </Button>
                                            </ButtonGroup>
                                        </span>
                                    </>
                                )}
                            </div>
                            <div className="vr d-none d-xl-inline-block"></div>
                            <Stack direction="horizontal" gap={1}>
                                <span>Demographics:</span>
                                <span className="fw-bold">
                                    {demo?.map(demoEntry => `${demoEntry.data_type === 1 ? "Online: " : "Cover: "} ${demoEntry.name}`).join(", ")}
                                </span>
                            </Stack>
                            <div className="vr d-none d-xl-inline-block"></div>
                            <Stack direction="horizontal" gap={1}>
                                <span>Bits: </span>
                                <span className="fw-bold">{collection?.[0]?.bit_count}</span>
                            </Stack>
                            <div className="flex-grow-1"></div>
                            <Stack direction="horizontal" gap={2}>
                                <Button
                                    type="submit"
                                    className="btn-ra-yellow py-1 mb-xl-1 "
                                    onClick={() => {
                                        setOpenEditPage(true);
                                        setEditCollectionData(collection);
                                    }}>
                                    Edit - {collection[0]?.collection_name}
                                </Button>
                            </Stack>
                        </Stack>
                        {excludedDates && (
                            <>
                                <Stack gap={1} direction="horizontal" className="flex-wrap">
                                    <span>Excluded days:</span>
                                    <span className="fw-bold">{excludedDates}</span>
                                </Stack>
                            </>
                        )}
                        <Stack direction="horizontal" gap={1} className=" my-2 flex-wrap-reverse">
                            <Col xs={12} xl={5}>
                                <InputGroup>
                                    <InputGroup.Text className="input-icon">
                                        <Search />
                                    </InputGroup.Text>
                                    <Form.Control
                                        value={searchValue}
                                        onChange={e => setSearchValue(e.target.value)}
                                        placeholder="search bit name, demographics, date or duration"
                                    />
                                </InputGroup>
                            </Col>
                            <div className=" ms-xl-auto">
                                <span className="me-2">Download:</span>
                                <ExportData
                                    data={csvData}
                                    headers={csvHeaders}
                                    filename={`Freestyledeltas for ${collection?.[0]?.collection_name} ${getRadioName(collection[0]?.radio_id)}`}
                                />
                            </div>
                            <ConfirmPrompt
                                show={deleteCollectionId}
                                handleClose={() => setDeleteCollectionId(null)}
                                handleConfirm={async () => await deleteCollection(deleteCollectionId)}
                            />
                        </Stack>
                    </>
                )}
                {showStack ? (
                    <Table size="sm" hover className="freestyle-deltas-table" responsive>
                        <thead>
                            <tr>
                                <th className="col-3" onClick={() => requestSort("bit_name")}>
                                    <Stack direction="horizontal">
                                        <span>
                                            Bit Name
                                            <RATooltip
                                                tooltipElement={<PlayCircleFill />} // Using a different tooltip element
                                                className="ms-1">
                                                <p>Listen in to the audio where available. We usually store audio for 3 months</p>
                                            </RATooltip>
                                        </span>
                                        <span className="col-1">
                                            {sortConfig?.key === "bit_name" && (sortConfig.direction === "descending" ? <CaretDownFill /> : <CaretUpFill />)}
                                        </span>
                                    </Stack>
                                </th>
                                <th className="col-1" onClick={() => requestSort("demo_id")}>
                                    <Stack direction="horizontal">
                                        <span>Demographics</span>
                                        <span className="col-1">
                                            {sortConfig?.key === "demo_id" && (sortConfig.direction === "descending" ? <CaretDownFill /> : <CaretUpFill />)}
                                        </span>
                                    </Stack>
                                </th>
                                <th className="col-1 " onClick={() => requestSort("date")}>
                                    <Stack direction="horizontal" className=" justify-content-center text-center">
                                        <span>Date</span>
                                        <span className="col-1">
                                            {sortConfig?.key === "date" && (sortConfig.direction === "descending" ? <CaretDownFill /> : <CaretUpFill />)}
                                        </span>
                                    </Stack>
                                </th>
                                <th className="col-1 " onClick={() => requestSort("start_time")}>
                                    <Stack direction="horizontal" className=" justify-content-center text-center">
                                        <span className="offset-1">Start Time</span>
                                        <span className="col-1">
                                            {sortConfig?.key === "start_time" && (sortConfig.direction === "descending" ? <CaretDownFill /> : <CaretUpFill />)}
                                        </span>
                                    </Stack>
                                </th>
                                <th className="col-1" onClick={() => requestSort("duration")}>
                                    <Stack direction="horizontal" className=" justify-content-center text-center">
                                        <span className="">Duration</span>
                                        <span className="col-1 ">
                                            {sortConfig?.key === "duration" && (sortConfig.direction === "descending" ? <CaretDownFill /> : <CaretUpFill />)}
                                        </span>
                                    </Stack>
                                </th>
                                <th className="col-1" onClick={() => requestSort("results.delta")}>
                                    <Stack direction="horizontal" className=" justify-content-center text-center">
                                        <span className=" d-flex flex-wrap-reverse column-gap-1 justify-content-center offset-1 align-items-center">
                                            Delta
                                            <RATooltip>
                                                <p>Delta is a mathematical term, meaning ”change”.</p>
                                                <p>
                                                    When we calculate the over- or under performance of a song or other on air content, we do it by finding the
                                                    change in listening behavior whenever that item is played.
                                                </p>
                                                <p>
                                                    This result also accounts for variables like time of day, holidays, or other irregularities that could
                                                    affect the result. What is left is a pure difference in listening behavior.
                                                </p>
                                            </RATooltip>
                                        </span>
                                        <span className="col-1">
                                            {sortConfig?.key === "results.delta" &&
                                                (sortConfig.direction === "descending" ? <CaretDownFill /> : <CaretUpFill />)}
                                        </span>
                                    </Stack>
                                </th>
                                <th className="col-1" onClick={() => requestSort("results.gain_loss")}>
                                    <Stack direction="horizontal" className=" justify-content-center text-center ">
                                        <span className=" d-flex flex-wrap-reverse column-gap-1 justify-content-center offset-1 align-items-center">
                                            Gain/loss
                                            <RATooltip>
                                                Average of ratings gained during break minus average ratings gained during break (basically deltas as an
                                                absolute number).
                                            </RATooltip>
                                        </span>
                                        <span className="col-1">
                                            {sortConfig?.key === "results.gain_loss" &&
                                                (sortConfig.direction === "descending" ? <CaretDownFill /> : <CaretUpFill />)}
                                        </span>
                                    </Stack>
                                </th>
                                <th className="col-1" onClick={() => requestSort("results.impressions")}>
                                    <Stack direction="horizontal" className=" justify-content-center text-center">
                                        <span>Impression</span>
                                        <span className="col-1">
                                            {sortConfig?.key === "results.impressions" &&
                                                (sortConfig.direction === "descending" ? <CaretDownFill /> : <CaretUpFill />)}
                                        </span>
                                    </Stack>
                                </th>
                                <th className="col-1" onClick={() => requestSort("results.delta_listeners")}>
                                    <Stack direction="horizontal" className=" justify-content-center text-center">
                                        <span className="d-flex column-gap-1 justify-content-center offset-1 align-items-center">
                                            Delta Listeners
                                            <RATooltip>
                                                Average Listeners gained or lost during the break (basically the ratings change part of gain/loss, but without
                                                taking into account the average).
                                            </RATooltip>
                                        </span>
                                        <span className="col-1">
                                            {sortConfig?.key === "results.delta_listeners" &&
                                                (sortConfig.direction === "descending" ? <CaretDownFill /> : <CaretUpFill />)}
                                        </span>
                                    </Stack>
                                </th>
                                <th className="col-1"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedItems.length ? (
                                sortedItems.map((x, itemIndex) => (
                                    <tr key={`${x.bit_id}_${x.demo_id}`}>
                                        <td className="d-flex justify-content-between align-items-center fw-bold">
                                            {selectedItem?.bit_id === x.bit_id ? (
                                                <>
                                                    <Form.Control
                                                        value={bitNameInputValue}
                                                        onChange={e => setBitNameInputValue(e.target.value)}
                                                        autoFocus
                                                        onKeyDown={e => {
                                                            if (e.key === "Enter") {
                                                                updateBitName(x, bitNameInputValue);
                                                            } else if (e.key === "Escape") {
                                                                setSelectedItem(null);
                                                            }
                                                        }}
                                                    />
                                                    <ButtonGroup size="sm">
                                                        <Button variant="success" title="Save" onClick={() => updateBitName(x, bitNameInputValue)}>
                                                            <CheckLg />
                                                        </Button>
                                                        <Button variant="danger" title="Discard" onClick={() => setSelectedItem(null)}>
                                                            <XLg />
                                                        </Button>
                                                    </ButtonGroup>
                                                </>
                                            ) : (
                                                <>
                                                    <span>
                                                        <Suspense>
                                                            <Await resolve={radiosRecordSettings}>
                                                                {resolvedRadiosRecordSettings => (
                                                                    <AudioPlayer
                                                                        getAudioUrl={async (...args) =>
                                                                            await getAudioUrl(x.start_datetime, x.end_datetime, ...args)
                                                                        }
                                                                        streamDelay={
                                                                            resolvedRadiosRecordSettings?.data.find(x => x.radio_id === collection[0].radio_id)
                                                                                ?.stream_delay ?? 0
                                                                        }
                                                                        disabled={
                                                                            resolvedRadiosRecordSettings?.data.find(x => x.radio_id === collection[0].radio_id)
                                                                                ?.audio_days_stored <=
                                                                            (new Date() - new Date(x.start_datetime)) / (1000 * 60 * 60 * 24)
                                                                                ? true
                                                                                : false
                                                                        }
                                                                    />
                                                                )}
                                                            </Await>
                                                        </Suspense>
                                                        <span className="ms-2">{x.bit_name}</span>
                                                    </span>
                                                    <ButtonGroup size="sm" className="me-3">
                                                        <Button
                                                            variant=""
                                                            title={"Edit"}
                                                            onClick={() => {
                                                                setSelectedItem(x);
                                                                setBitNameInputValue(x.bit_name);
                                                            }}>
                                                            <PencilSquare />
                                                        </Button>
                                                    </ButtonGroup>
                                                </>
                                            )}
                                        </td>

                                        <td className="">{x.demo_name}</td>
                                        <td className="text-center">{moment(x.start_datetime).format("YYYY-MM-DD")}</td>
                                        <td className="text-center">{moment(x.start_datetime).format("HH:mm:ss")}</td>
                                        <td className="text-center">{moment.utc(moment(x.end_datetime).diff(moment(x.start_datetime))).format("mm:ss")}</td>
                                        <td
                                            className={`text-center fw-bold ${
                                                x.results?.delta > 0 ? "text-success" : x.results?.delta < 0 ? "text-danger" : "text-secondary"
                                            }`}>
                                            {x.results?.delta ?? "-"}
                                        </td>
                                        <td className="text-center">{x.results?.gain_loss}</td>
                                        <td className="text-center">{x.results?.impressions}</td>
                                        <td className="text-center">
                                            <>{x.results?.delta_listeners}</>
                                        </td>
                                        <td>
                                            <ButtonGroup size="sm ms-5">
                                                <Button variant="" title={"Delete"} onClick={() => setDeleteCollectionBit(x)}>
                                                    <TrashFill className="text-danger" />
                                                </Button>
                                            </ButtonGroup>
                                        </td>
                                        <ConfirmPrompt
                                            show={deleteCollectionBit}
                                            handleClose={() => setDeleteCollectionBit(null)}
                                            handleConfirm={async () => await deleteBit(deleteCollectionBit)}
                                        />
                                    </tr>
                                ))
                            ) : (
                                <EmptyTableRow />
                            )}
                        </tbody>
                    </Table>
                ) : (
                    <Table size="sm" hover className="freestyle-deltas-table-edit-results" responsive>
                        <thead>
                            <tr>
                                <th className="col-3" onClick={() => requestSort("bit_name")}>
                                    <Stack direction="horizontal">
                                        <span>Bit Name</span>
                                        <span className="col-1">
                                            {sortConfig?.key === "bit_name" && (sortConfig.direction === "descending" ? <CaretDownFill /> : <CaretUpFill />)}
                                        </span>
                                    </Stack>
                                </th>
                                <th className="col-3 " onClick={() => requestSort("date")}>
                                    <Stack direction="horizontal" className=" justify-content-center text-center">
                                        <span>Date</span>
                                        <span className="col-1">
                                            {sortConfig?.key === "date" && (sortConfig.direction === "descending" ? <CaretDownFill /> : <CaretUpFill />)}
                                        </span>
                                    </Stack>
                                </th>
                                <th className="col-3 " onClick={() => requestSort("start_time")}>
                                    <Stack direction="horizontal" className=" justify-content-center text-center">
                                        <span className="offset-1">Start Time</span>
                                        <span className="col-1">
                                            {sortConfig?.key === "start_time" && (sortConfig.direction === "descending" ? <CaretDownFill /> : <CaretUpFill />)}
                                        </span>
                                    </Stack>
                                </th>
                                <th className="col-2" onClick={() => requestSort("duration")}>
                                    <Stack direction="horizontal" className=" justify-content-center text-center">
                                        <span className="">Duration</span>
                                        <span className="col-1 ">
                                            {sortConfig?.key === "duration" && (sortConfig.direction === "descending" ? <CaretDownFill /> : <CaretUpFill />)}
                                        </span>
                                    </Stack>
                                </th>
                                <th className="col-1"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedItems.length ? (
                                sortedItems.map((x, itemIndex) => (
                                    <tr key={`${x.bit_id}_${x.demo_id}`}>
                                        <td className="d-flex justify-content-between align-items-center fw-bold">
                                            {selectedItem?.bit_id === x.bit_id ? (
                                                <>
                                                    <Form.Control value={bitNameInputValue} onChange={e => setBitNameInputValue(e.target.value)} />
                                                    <ButtonGroup size="sm">
                                                        <Button variant="success" title="Save" onClick={() => updateBitName(x, bitNameInputValue)}>
                                                            <CheckLg />
                                                        </Button>
                                                        <Button variant="danger" title="Discard" onClick={() => setSelectedItem(null)}>
                                                            <XLg />
                                                        </Button>
                                                    </ButtonGroup>
                                                </>
                                            ) : (
                                                <>
                                                    {x.bit_name}
                                                    <ButtonGroup size="sm" className="me-3">
                                                        <Button
                                                            variant=""
                                                            title={"Edit"}
                                                            onClick={() => {
                                                                setSelectedItem(x);
                                                                setBitNameInputValue(x.bit_name);
                                                            }}>
                                                            <PencilSquare />
                                                        </Button>
                                                    </ButtonGroup>
                                                </>
                                            )}
                                        </td>
                                        <td className="text-center">{moment(x.start_datetime).format("YYYY-MM-DD")}</td>
                                        <td className="text-center">{moment(x.start_datetime).format("HH:mm:ss")}</td>
                                        <td className="text-center">{moment.utc(moment(x.end_datetime).diff(moment(x.start_datetime))).format("mm:ss")}</td>

                                        <td className="text-center me-5">
                                            <Button size="sm" variant="" title={"Delete"} onClick={() => setDeleteCollectionBit(x)}>
                                                <TrashFill className="text-danger" />
                                            </Button>
                                        </td>
                                        <ConfirmPrompt
                                            show={deleteCollectionBit}
                                            handleClose={() => setDeleteCollectionBit(null)}
                                            handleConfirm={async () => await deleteBit(deleteCollectionBit)}
                                        />
                                    </tr>
                                ))
                            ) : (
                                <EmptyTableRow />
                            )}
                        </tbody>
                    </Table>
                )}
            </div>
        </>
    );
};

export default FreestyleDeltasTableDisplay;
